import React from 'react';
import { Link } from 'react-router-dom';

/**
 * 회원 페이지는 모바일 앱 전용. 웹 브라우저로 진입한 경우 이 화면을 띄워
 * 사용자를 앱 설치로 유도한다.
 */
const ANDROID_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.dream_drums.app';
const IOS_STORE_URL =
  'https://apps.apple.com/app/dream-drums/id0000000000'; // 출시 후 실제 ID 로 교체

const AppOnly: React.FC = () => {
  return (
    <div className="ao-root">
      <style>{`
        .ao-root {
          --ink: #0a0a0c;
          --paper: #f5f1ea;
          --muted: #8a857d;
          --rule: #1f1f23;
          --accent: #f5b800;
          position: relative;
          min-height: 100vh;
          min-height: 100svh;
          background: var(--ink);
          color: var(--paper);
          font-family: "Pretendard", -apple-system, BlinkMacSystemFont,
            "Apple SD Gothic Neo", "Helvetica Neue", system-ui, sans-serif;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        /* 매우 옅은 그레인/노이즈 느낌의 라디얼 — 단조로움 방지 */
        .ao-root::before {
          content: '';
          position: absolute; inset: 0;
          background:
            radial-gradient(60% 50% at 50% 0%, rgba(245,184,0,0.10), transparent 70%),
            radial-gradient(80% 60% at 50% 100%, rgba(255,255,255,0.04), transparent 70%);
          pointer-events: none;
        }

        /* 상단 메타 바 (편집디자인 헤더 느낌) */
        .ao-meta {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px 0;
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--muted);
          font-weight: 500;
        }
        .ao-meta .ao-dot {
          display: inline-block;
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--accent);
          margin-right: 8px;
          vertical-align: middle;
          box-shadow: 0 0 12px var(--accent);
        }
        .ao-meta .ao-issue { color: rgba(245,241,234,0.5); }

        /* 본문 컨테이너 */
        .ao-stage {
          position: relative;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 32px 28px 28px;
          max-width: 560px;
          margin: 0 auto;
          width: 100%;
        }

        /* 워드마크 자리 */
        .ao-wordmark {
          font-family: "Times New Roman", "Noto Serif KR", Georgia, serif;
          font-style: italic;
          font-weight: 400;
          font-size: 18px;
          letter-spacing: 0.02em;
          color: rgba(245,241,234,0.85);
          margin-bottom: 56px;
        }
        .ao-wordmark .em { color: var(--accent); font-style: normal; font-weight: 600; letter-spacing: -0.02em; }

        /* 헤드라인 */
        .ao-eyebrow {
          font-size: 12px;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          color: var(--accent);
          font-weight: 600;
          margin-bottom: 22px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .ao-eyebrow::before {
          content: '';
          width: 28px;
          height: 1px;
          background: var(--accent);
          display: block;
        }

        .ao-head {
          font-size: clamp(38px, 11vw, 60px);
          line-height: 0.98;
          letter-spacing: -0.045em;
          font-weight: 800;
          margin: 0 0 26px;
          color: var(--paper);
        }
        .ao-head .br { display: block; }
        .ao-head .ital {
          font-family: "Times New Roman", "Noto Serif KR", Georgia, serif;
          font-style: italic;
          font-weight: 500;
          letter-spacing: -0.02em;
          color: var(--accent);
        }

        .ao-lede {
          font-size: 15px;
          line-height: 1.65;
          color: rgba(245,241,234,0.62);
          margin: 0 0 44px;
          max-width: 420px;
        }

        /* 가로 룰 */
        .ao-rule {
          height: 1px;
          background: var(--rule);
          margin: 0 0 28px;
        }

        /* 스토어 CTA */
        .ao-cta-label {
          font-size: 11px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--muted);
          margin-bottom: 14px;
        }

        .ao-stores {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        @media (max-width: 380px) {
          .ao-stores { grid-template-columns: 1fr; }
        }
        .ao-store {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 16px 14px;
          border-radius: 999px;
          background: var(--paper);
          color: #0a0a0c;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: transform .18s ease, background .2s ease, color .2s ease;
        }
        .ao-store:hover { background: var(--accent); }
        .ao-store:active { transform: scale(0.985); }
        .ao-store .ic { width: 18px; height: 18px; flex-shrink: 0; display: block; }
        .ao-store .lbl { display: flex; flex-direction: column; line-height: 1.05; }
        .ao-store .top { font-size: 9px; font-weight: 500; letter-spacing: .16em; text-transform: uppercase; opacity: .58; }
        .ao-store .bot { font-size: 14.5px; font-weight: 700; letter-spacing: -.015em; margin-top: 2px; }

        /* 하단 푸터 */
        .ao-foot {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px 22px;
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--muted);
          border-top: 1px solid var(--rule);
          margin-top: auto;
        }
        .ao-foot a {
          color: rgba(245,241,234,0.8);
          text-decoration: none;
          font-weight: 500;
          letter-spacing: 0.18em;
          transition: color .15s ease;
        }
        .ao-foot a:hover { color: var(--accent); }

        /* 우상단 — 큰 숫자 데코 (잡지 페이지 번호 느낌) */
        .ao-num {
          position: absolute;
          top: 56px;
          right: 24px;
          font-family: "Times New Roman", "Noto Serif KR", Georgia, serif;
          font-style: italic;
          font-size: clamp(70px, 22vw, 130px);
          line-height: 1;
          color: rgba(245,241,234,0.05);
          font-weight: 400;
          letter-spacing: -0.04em;
          user-select: none;
          pointer-events: none;
          z-index: 0;
        }

        .ao-stage > * { position: relative; z-index: 1; }
      `}</style>

      <div className="ao-meta">
        <span>
          <span className="ao-dot" />
          Mobile Only
        </span>
        <span className="ao-issue">N° 01 · KR</span>
      </div>

      <div className="ao-stage">
        <div className="ao-num">01</div>

        <div className="ao-wordmark">
          Dream <span className="em">Drums</span>
        </div>

        <div className="ao-eyebrow">For Members</div>

        <h1 className="ao-head">
          <span className="br">앱에서</span>
          <span className="br ital">만나요.</span>
        </h1>

        <p className="ao-lede">
          회원 페이지는 전용 앱에서만 제공돼요.
          <br />
          더 빠르고, 더 안전하고, 알림도 놓치지 않게.
        </p>

        <div className="ao-rule" />

        <div className="ao-cta-label">Get the App</div>
        <div className="ao-stores">
          <a
            className="ao-store"
            href={IOS_STORE_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="App Store 에서 다운로드"
          >
            <svg className="ic" viewBox="0 0 384 512" aria-hidden="true">
              <path
                fill="currentColor"
                d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM260.3 80.1c25.2-29.9 22.9-57.1 22.2-67.1-22.4 1.3-48.3 15.3-63 32.5-16.2 18.4-25.7 41.2-23.7 65.7 24.2 1.9 46.3-10.5 64.5-31.1z"
              />
            </svg>
            <span className="lbl">
              <span className="top">Download on</span>
              <span className="bot">App Store</span>
            </span>
          </a>

          <a
            className="ao-store"
            href={ANDROID_STORE_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="Google Play 에서 다운로드"
          >
            <svg className="ic" viewBox="0 0 512 512" aria-hidden="true">
              <path fill="#34A853" d="M325.3 234.3 104.6 13l280.8 161-60.1 60.3z" />
              <path fill="#FBBC04" d="M104.6 499 325.3 277.7l60.1 60.3-280.8 161z" />
              <path fill="#EA4335" d="m325.3 234.3 60.1 60.3 80.2-46.4c10.1-5.9 10.1-19.7 0-25.6L385.4 174z" />
              <path fill="#4285F4" d="M104.6 13c-5.4 3.2-8.6 8.9-8.6 16v454c0 7.1 3.2 12.8 8.6 16l220.7-265z" />
            </svg>
            <span className="lbl">
              <span className="top">Get it on</span>
              <span className="bot">Google Play</span>
            </span>
          </a>
        </div>
      </div>

      <div className="ao-foot">
        <span>© Dream Drums</span>
        <Link to="/">← Home</Link>
      </div>
    </div>
  );
};

export default AppOnly;
